<template>
	<section class="stack">
		<!-- Section Title -->
		<div
			class="section-title"
			:data-test="headingDataTest"
		>
			<v-tooltip max-width="500px">
				<template v-slot:activator="{ props }">
					<div
						class="sgnl-flex sgnl-align-center sgnl-h6-semibold sgnl-gap-8"
						v-bind="props"
					>
						{{ sectionTitle }}
						<Information16 class="section-title-info" />
					</div>
				</template>
				{{ sectionDescription }}
			</v-tooltip>
		</div>

		<ErrorEmptyState
			v-if="!dataLoaded && updateStatus?.error"
			:title="`Could not load ${sectionTitle}`"
			message="Often this is a temporary network or connectivity issue."
			:retrying="!!updateStatus?.loading"
			bordered
			@retry="$emit('retry')"
		/>
		<slot
			v-else-if="!dataLoaded"
			name="skeleton"
		/>

		<!-- Card list -->
		<v-slide-y-transition
			v-if="hasItems"
			group
			tag="div"
			class="card-list"
		>
			<slot name="items" />
		</v-slide-y-transition>

		<!-- Show if no items available (only after data has been loaded) -->
		<template v-else-if="dataLoaded && !hasItems">
			<div
				class="empty-state-container"
				:data-test="emptyDataTest"
			>
				<img
					src="@/assets/icons/common/empty-state-48.svg"
					width="48"
					height="48"
					alt=""
				/>
				<span class="empty-state-text sgnl-text-color-default">{{ noItemsMessage }}</span>
			</div>
		</template>

		<!-- Show if user can load more items -->
		<template v-if="canLoadMore">
			<div class="sgnl-flex sgnl-justify-center">
				<span
					v-if="hasMoreToLoad"
					:data-test="loadMoreDataTest"
				>
					<slot name="loadMoreButton" />
				</span>
			</div>
		</template>
	</section>
</template>

<script lang="ts">
import { RemoteAccessState } from "@/types/general";
import { defineComponent, PropType } from "vue";

// Components
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import Information16 from "@carbon/icons-vue/es/information/16";

export default defineComponent({
	name: "ListDisplay",
	components: { ErrorEmptyState, Information16 },
	props: {
		sectionTitle: { type: String, required: true },
		sectionDescription: { type: String, required: true },
		updateStatus: { type: Object as PropType<RemoteAccessState> },
		continuationKey: { type: String },
		noItemsMessage: { type: String, required: true },
		canLoadMore: { type: Boolean, default: false },
		dataLoaded: { type: Boolean, default: true },
		hasItems: { type: Boolean, required: true },
		/** data-test attribute for the section heading element */
		headingDataTest: { type: String, default: "" },
		/** data-test attribute for the empty-state container */
		emptyDataTest: { type: String, default: "" },
		/** data-test attribute for the load-more button wrapper */
		loadMoreDataTest: { type: String, default: "" },
	},
	emits: { retry: () => true },
	computed: {
		hasMoreToLoad(): boolean {
			return this.continuationKey !== undefined;
		},
	},
});
</script>

<style lang="scss" scoped>
.card-list {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.empty-state-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: var(--sgnl-space-8);
	align-self: stretch;
	width: 100%;
	min-height: 162px;
	padding: var(--sgnl-space-16);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-primary-border);
	border-radius: var(--sgnl-border-radius-6);
}

.empty-state-text {
	text-align: center;
	font-size: var(--sgnl-font-size-paragraph-s);
	font-style: normal;
	font-weight: 400;
	line-height: var(--sgnl-font-line-height-paragraph-s);
	letter-spacing: var(--sgnl-font-letter-spacing0);
}
</style>
